import * as React from "react"

import * as Popover from "@radix-ui/react-popover"
import * as SelectPrimitive from "@radix-ui/react-select"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "../../cn"
import { useListboxNavigation } from "../../hooks/useListboxNavigation"
import { useScrollLockExemption } from "../../hooks/useScrollLockExemption"
import { ChevronDownIcon } from "../ChevronDownIcon/ChevronDownIcon"
import { Tooltip } from "../Tooltip/Tooltip"
import { resultCountMessage, SearchResultsStatus } from "./SearchResultsStatus"

const selectTriggerVariants = cva(
  [
    "coval-field flex h-control w-full cursor-pointer items-center justify-between gap-2 rounded-md px-2",
    "text-sm text-coval-text placeholder:text-coval-text-muted",
    "transition-colors duration-150",
    "disabled:cursor-default disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-surface-default",
          "border border-surface-1",
          "hover:bg-surface-contrast disabled:hover:bg-surface-default",
        ],
        secondary: [
          "bg-surface-default",
          "border border-coval-border",
          "hover:bg-surface-contrast hover:border-coval-border-active",
          "disabled:hover:bg-surface-default disabled:hover:border-coval-border",
        ],
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

const selectContentVariants = cva([
  "relative z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] rounded-md",
  "bg-surface-default text-coval-text",
  "border border-coval-border shadow-md",
])

/* ── Root ──────────────────────────────────────────────────── */

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

/* ── Trigger ───────────────────────────────────────────────── */

type AccessibleName =
  | { "aria-label": string; "aria-labelledby"?: string }
  | { "aria-label"?: string; "aria-labelledby": string }

/**
 * The trigger's text is the selected value, never the field's name, so the
 * name comes from `aria-label` or from a visible label via `aria-labelledby`.
 */
export type SelectTriggerProps = Omit<
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
  "aria-label" | "aria-labelledby"
> &
  VariantProps<typeof selectTriggerVariants> &
  AccessibleName & {
    error?: boolean
    /**
     * Drops the chevron, for triggers whose value is short enough that the
     * affordance costs more room than it earns.
     */
    hideIcon?: boolean
  }

const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, variant, error, hideIcon, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    data-invalid={error || undefined}
    className={cn(
      selectTriggerVariants({ variant }),
      error &&
        "border-coval-danger-border hover:border-coval-danger-border focus:border-coval-danger-border bg-coval-danger-surface text-coval-danger-on-surface hover:bg-coval-danger-surface disabled:hover:bg-coval-danger-surface",
      className,
    )}
    {...props}
  >
    {/* The value takes the room the chevron leaves and ellipsizes there: a
        long option must not wrap the trigger onto a second line, which would
        break the control's fixed height and the row it sits in. */}
    <span className="min-w-0 flex-1 truncate text-left">{children}</span>
    {!hideIcon && (
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon />
      </SelectPrimitive.Icon>
    )}
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

/* ── Content ───────────────────────────────────────────────── */

const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        selectContentVariants(),
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(
          "overflow-y-auto p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

/* ── Label ─────────────────────────────────────────────────── */

const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-sm font-medium text-coval-text-muted",
      className,
    )}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

/* ── Item ──────────────────────────────────────────────────── */

export interface SelectItemProps extends React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Item
> {
  description?: React.ReactNode
  icon?: React.ReactNode
}

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({ className, children, description, icon, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none rounded-sm py-1.5 px-2",
      "text-sm text-coval-text",
      "outline-none",
      "hover:bg-surface-contrast focus:bg-surface-contrast",
      "data-[state=checked]:bg-surface-contrast",
      "data-[disabled]:cursor-default data-[disabled]:opacity-50 data-[disabled]:hover:bg-transparent",
      description ? "items-start gap-2" : "items-center gap-2",
      className,
    )}
    {...props}
  >
    {/* Decorative: the item's text names it. */}
    {icon && (
      <span
        aria-hidden="true"
        className="flex shrink-0 items-center text-coval-text-muted"
      >
        {icon}
      </span>
    )}
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      {description && (
        <span className="text-xs text-coval-text-muted leading-snug">
          {description}
        </span>
      )}
    </div>
    <SelectPrimitive.ItemIndicator className="ml-auto shrink-0">
      <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
        <CheckIcon />
      </span>
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

/* ── Separator ─────────────────────────────────────────────── */

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-surface-1-hover", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

/* ── SelectDropdown (options-based, supports searchable) ──── */

const selectDropdownContentVariants = cva([
  "z-50 min-w-[var(--radix-popover-trigger-width)] rounded-md p-1",
  "bg-surface-default text-coval-text",
  "border border-coval-border shadow-md",
])

export interface SelectDropdownOption {
  value: string
  label: string
  /** Plain text: it renders inside the option's button, which holds no controls. */
  description?: string
  disabled?: boolean
  testId?: string
  dataAttributes?: {
    [attributeName: `data-${string}`]: string | undefined
  }
}

export type SelectDropdownProps = VariantProps<typeof selectTriggerVariants> &
  AccessibleName & {
    options: SelectDropdownOption[]
    value?: string
    defaultValue?: string
    onValueChange?: (value: string) => void
    /**
     * Shows a clear button while a value is selected. The handler resets the
     * value; an uncontrolled dropdown also drops back to its placeholder.
     * With it set, `className` sizes the wrapper that holds the trigger and
     * the clear button, so the button stays aligned to the trigger's edge.
     */
    onClear?: () => void
    placeholder?: string
    searchable?: boolean
    searchPlaceholder?: string
    error?: boolean
    /** Announces the field as required. It sets no native constraint. */
    required?: boolean
    disabled?: boolean
    className?: string
    "data-testid"?: string
    searchInputTestId?: string
  }

function SelectDropdown({
  options,
  value: controlledValue,
  defaultValue,
  onValueChange,
  onClear,
  placeholder = "Select...",
  searchable = false,
  searchPlaceholder = "Search...",
  variant,
  error,
  required,
  disabled,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "data-testid": dataTestId,
  searchInputTestId,
}: SelectDropdownProps) {
  const [internalValue, setInternalValue] = React.useState<string | undefined>(
    defaultValue,
  )
  const [search, setSearch] = React.useState("")
  const [open, setOpen] = React.useState(false)
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const scrollableListRef = useScrollLockExemption<HTMLDivElement>()
  const listboxId = React.useId()
  const optionIdPrefix = React.useId()
  const clearButtonId = React.useId()

  const isControlled = controlledValue !== undefined
  const selected = isControlled ? controlledValue : internalValue

  const handleSelect = React.useCallback(
    (optionValue: string) => {
      if (!isControlled) {
        setInternalValue(optionValue)
      }
      onValueChange?.(optionValue)
      setSearch("")
      setOpen(false)
    },
    [isControlled, onValueChange],
  )

  const displayText = React.useMemo(() => {
    if (!selected) {
      return null
    }
    return options.find((o) => o.value === selected)?.label ?? null
  }, [selected, options])

  const filteredOptions = React.useMemo(() => {
    if (!searchable || search.trim() === "") {
      return options
    }
    const query = search.toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(query))
  }, [options, search, searchable])

  const selectedIndex = filteredOptions.findIndex((o) => o.value === selected)
  const firstEnabledIndex = filteredOptions.findIndex((o) => !o.disabled)
  const { activeIndex, setActiveIndex, onKeyDown } = useListboxNavigation({
    items: filteredOptions,
    open,
    initialIndex:
      selectedIndex >= 0 && !filteredOptions[selectedIndex].disabled
        ? selectedIndex
        : firstEnabledIndex,
    onSelect: (index) => handleSelect(filteredOptions[index].value),
    hasTextInput: searchable,
  })
  const optionId = (index: number) => `${optionIdPrefix}-option-${index}`
  const activeOptionId =
    open && activeIndex >= 0 && activeIndex < filteredOptions.length
      ? optionId(activeIndex)
      : undefined

  React.useEffect(() => {
    if (activeOptionId) {
      document
        .getElementById(activeOptionId)
        ?.scrollIntoView?.({ block: "nearest" })
    }
  }, [activeOptionId])

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSearch("")
    }
  }, [])

  const showClear = !!onClear && !!selected && !disabled

  const handleClear = React.useCallback(() => {
    if (!isControlled) {
      setInternalValue(undefined)
    }
    setSearch("")
    setOpen(false)
    onClear?.()
  }, [isControlled, onClear])

  const errorClassName =
    error &&
    "border-coval-danger-border hover:border-coval-danger-border focus:border-coval-danger-border focus-within:border-coval-danger-border bg-coval-danger-surface text-coval-danger-on-surface hover:bg-coval-danger-surface disabled:hover:bg-coval-danger-surface"

  const trigger = (
    <Popover.Trigger asChild disabled={disabled}>
      <button
        type="button"
        role="combobox"
        disabled={disabled}
        aria-invalid={error || undefined}
        data-invalid={error || undefined}
        aria-required={required || undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        // Points at the listbox only while it is rendered; a closed menu has
        // no element for the reference to resolve to.
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        // Without a search field focus never leaves the trigger: it points at
        // the active option and takes the listbox keys itself, as the
        // select-only combobox pattern describes.
        aria-activedescendant={searchable ? undefined : activeOptionId}
        onKeyDown={
          searchable
            ? undefined
            : (event) => {
                if (open) {
                  onKeyDown(event)
                } else if (
                  event.key === "ArrowDown" ||
                  event.key === "ArrowUp"
                ) {
                  event.preventDefault()
                  setOpen(true)
                }
              }
        }
        data-testid={dataTestId}
        className={
          onClear
            ? "flex h-full min-w-0 flex-1 cursor-pointer items-center justify-between gap-2 rounded-md pl-2 pr-1 text-left disabled:cursor-default"
            : cn(selectTriggerVariants({ variant }), errorClassName, className)
        }
      >
        <span className="truncate">
          {displayText ?? (
            <span
              className={
                error
                  ? "text-coval-danger-on-surface/80"
                  : "text-coval-text-muted"
              }
            >
              {placeholder}
            </span>
          )}
        </span>
        {/* The clear button takes the chevron's place while it shows. */}
        {!showClear && <ChevronDownIcon />}
      </button>
    </Popover.Trigger>
  )

  // With a clear button the field's box is a wrapper holding the trigger and
  // the button side by side: a button cannot nest inside the trigger button,
  // and laying one over the other would leave each target partly covered.
  const field = onClear ? (
    <Popover.Anchor asChild>
      <div
        data-invalid={error || undefined}
        className={cn(
          selectTriggerVariants({ variant }),
          "cursor-default gap-0 px-0 pr-1",
          "has-[:disabled]:opacity-50 has-[:disabled]:hover:bg-surface-default",
          errorClassName,
          className,
        )}
      >
        {trigger}
        {showClear && (
          <Tooltip content="Clear">
            <button
              type="button"
              id={clearButtonId}
              onClick={handleClear}
              aria-label={ariaLabel ? `Clear ${ariaLabel}` : "Clear"}
              // A self-reference reads this button's own "Clear" label, then
              // the field's label, so each clear button names the field it
              // clears.
              aria-labelledby={
                ariaLabel ? undefined : `${clearButtonId} ${ariaLabelledBy}`
              }
              className="flex size-[24px] shrink-0 cursor-pointer items-center justify-center rounded-sm text-coval-text-muted hover:text-coval-text [&_svg]:size-3.5"
            >
              <X aria-hidden="true" />
            </button>
          </Tooltip>
        )}
      </div>
    </Popover.Anchor>
  ) : (
    trigger
  )

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      {field}

      <Popover.Portal>
        <Popover.Content
          className={cn(selectDropdownContentVariants())}
          // The popover is a dialog; it takes the field's name.
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          sideOffset={4}
          align="start"
          // Focus goes to the search field or stays on the trigger, and the
          // active option is tracked through `aria-activedescendant`, so typing
          // keeps filtering while the arrow keys move through the results.
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            if (searchable) {
              searchInputRef.current?.focus()
            }
          }}
        >
          {searchable && (
            <div className="p-1">
              <div className="coval-field flex items-center gap-1.5 rounded-md border border-surface-1 bg-surface-default px-2">
                <SearchIcon />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  aria-controls={listboxId}
                  aria-activedescendant={activeOptionId}
                  data-testid={searchInputTestId}
                  className="h-control w-full bg-transparent text-sm text-coval-text placeholder:text-coval-text-muted"
                />
              </div>
            </div>
          )}
          <div
            ref={scrollableListRef}
            id={listboxId}
            className="max-h-60 overflow-y-auto"
            role="listbox"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            tabIndex={-1}
            // Focus stays on the trigger or search field, so a press on an
            // option, even a disabled one, never moves it into the list. A
            // press on the scrollbar is left alone so the list still drags.
            onMouseDown={(event) => {
              if ((event.target as Element).closest('[role="option"]')) {
                event.preventDefault()
              }
            }}
          >
            {filteredOptions.map((option, index) => {
              const isSelected = selected === option.value
              const isActive = index === activeIndex
              return (
                <button
                  type="button"
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  // Focus stays on the trigger or search field, which
                  // points here through `aria-activedescendant`.
                  tabIndex={-1}
                  aria-selected={isSelected}
                  aria-disabled={option.disabled || undefined}
                  data-active={isActive || undefined}
                  {...option.dataAttributes}
                  data-testid={option.testId}
                  onClick={() => {
                    if (!option.disabled) {
                      handleSelect(option.value)
                    }
                  }}
                  onPointerMove={() => {
                    if (!option.disabled && !isActive) {
                      setActiveIndex(index)
                    }
                  }}
                  className={cn(
                    "relative flex w-full cursor-pointer text-left select-none rounded-sm py-1.5 px-2",
                    "text-sm text-coval-text",
                    // One highlight follows the pointer and the arrow keys
                    // alike; the check mark is what marks the selection.
                    "data-[active]:bg-surface-contrast",
                    option.disabled && "cursor-default opacity-50",
                    option.description
                      ? "items-start gap-2"
                      : "items-center gap-2",
                  )}
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
                    <span className="truncate">{option.label}</span>
                    {option.description && (
                      <span className="text-xs leading-snug text-coval-text-muted">
                        {option.description}
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <span className="ml-auto flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                      <CheckIcon />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          <SearchResultsStatus
            emptyState={
              filteredOptions.length === 0 ? (
                <p className="px-2 py-4 text-center text-sm text-coval-text-muted">
                  No results
                </p>
              ) : null
            }
            announcement={
              searchable
                ? resultCountMessage(search, filteredOptions.length)
                : ""
            }
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

SelectDropdown.displayName = "SelectDropdown"

/* ── Inline icons (avoids lucide peer-dep in @coval/ui) ──── */

function SearchIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0 text-coval-text-muted"
    >
      <path
        d="M5.5 9.5C7.7091 9.5 9.5 7.7091 9.5 5.5C9.5 3.29086 7.7091 1.5 5.5 1.5C3.29086 1.5 1.5 3.29086 1.5 5.5C1.5 7.7091 3.29086 9.5 5.5 9.5Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.5 10.5L8.32 8.32"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* ── Exports ───────────────────────────────────────────────── */

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectDropdown,
  selectTriggerVariants,
  selectContentVariants,
  selectDropdownContentVariants,
}
